import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    LocationOn as LocationIcon,
    AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Стилизованные компоненты
const StatusChipWithMenu = styled(Chip)(({ theme, darkMode }) => ({
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: '0.75rem',
    fontWeight: 600,
    height: '24px',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark
    }
}));

const TableView = ({
    darkMode,
    getAllTasks,
    canDragTask,
    canEditTask,
    canDeleteTask,
    handleStatusChange,
    handleEditTask,
    handleDeleteTask,
    handleViewSpecs,
    handleFocusOnTaskArea,
    formatDate
}) => {
    const allTasks = getAllTasks();

    // Общие стили для заголовков таблицы
    const headerCellStyle = {
        background: darkMode ? '#1e1e1e' : 'rgba(248, 249, 250, 0.8)',
        fontWeight: 600,
        fontSize: { xs: '0.75rem', md: '0.875rem' },
        color: darkMode ? '#f5f7fa' : '#374151',
        borderBottom: darkMode
            ? '2px solid rgba(255, 255, 255, 0.12)'
            : '2px solid rgba(0, 0, 0, 0.12)',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        py: { xs: 0.5, md: 1 },
        px: { xs: 0.5, md: 1 }
    };

    // Общие стили для ячеек данных
    const dataCellStyle = {
        padding: { xs: '0.25rem', md: '0.5rem' },
        borderBottom: darkMode
            ? '1px solid rgba(255, 255, 255, 0.06)'
            : '1px solid rgba(0, 0, 0, 0.06)',
        color: darkMode ? '#f5f7fa' : '#333',
        fontSize: { xs: '0.75rem', md: '0.875rem' }
    };

    return (
        <Box sx={{
            height: '100%',
            width: '99%',
            p: { xs: 0.5, md: 2 }
        }}>
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: { xs: '4px', md: '8px' },
                    boxShadow: darkMode
                        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.08)',
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.05)',
                    '& .MuiTableCell-root': {
                        borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'
                    },
                    // Добавляем горизонтальный скролл для мобильных
                    overflowX: 'auto'
                }}
            >
                <Table sx={{ minWidth: { xs: 800, md: 1200 } }} stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                width: { xs: '50px', md: '80px' },
                                minWidth: { xs: '50px', md: '80px' }
                            }}>ID</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '120px', md: '150px' }
                            }}>Название</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '100px', md: '150px' },
                                display: { xs: 'none', md: 'table-cell' }
                            }}>Описание</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '80px', md: '120px' }
                            }}>Статус</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '80px', md: '100px' },
                                display: { xs: 'none', sm: 'table-cell' }
                            }}>Постановщик</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '80px', md: '100px' },
                                display: { xs: 'none', sm: 'table-cell' }
                            }}>Создано</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '80px', md: '100px' }
                            }}>Ответственный</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '80px', md: '100px' }
                            }}>Дедлайн</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '80px', md: '100px' },
                                display: { xs: 'none', lg: 'table-cell' }
                            }}>Редактор</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                minWidth: { xs: '80px', md: '100px' },
                                display: { xs: 'none', lg: 'table-cell' }
                            }}>Изменено</TableCell>
                            <TableCell align='center' sx={{
                                ...headerCellStyle,
                                width: { xs: '80px', md: '100px' },
                                minWidth: { xs: '80px', md: '100px' }
                            }}>Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allTasks.map((task) => (
                            <TableRow
                                key={task.id}
                                sx={{
                                    '&:hover': {
                                        bgcolor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
                                    },
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff'
                                }}
                            >
                                <TableCell align='center' sx={{
                                    ...dataCellStyle,
                                    fontWeight: 600,
                                    color: darkMode ? '#4fc3f7' : '#0288d1',
                                    width: { xs: '50px', md: '80px' },
                                    fontSize: { xs: '0.7rem', md: '0.875rem' }
                                }}>
                                    {task.id}
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    fontWeight: 600,
                                    minWidth: { xs: '120px', md: '150px' },
                                    maxWidth: { xs: '150px', md: '200px' },
                                    wordBreak: 'break-word'
                                }}>
                                    {task.title || task.name}
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    color: darkMode ? '#bbb' : '#555',
                                    maxWidth: { xs: '100px', md: '200px' },
                                    display: { xs: 'none', md: 'table-cell' }
                                }}>
                                    {task.description || task.desc ? (
                                        <Box
                                            sx={{
                                                '& strong, & b': {
                                                    fontWeight: 700,
                                                    color: darkMode ? '#fff' : '#1a1a1a'
                                                },
                                                '& em, & i': {
                                                    fontStyle: 'italic',
                                                    color: darkMode ? '#e0e0e0' : '#424242'
                                                },
                                                '& ul, & ol': {
                                                    paddingLeft: 1.5,
                                                    margin: 0,
                                                    color: darkMode ? '#bbb' : '#666'
                                                },
                                                '& li': {
                                                    listStyle: 'disc',
                                                    fontSize: '0.75rem',
                                                    marginBottom: '1px',
                                                    lineHeight: 1.2
                                                },
                                                '& code': {
                                                    backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                                    padding: '0.1rem 0.2rem',
                                                    borderRadius: '0.15rem',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.7em',
                                                    color: darkMode ? '#ff6b6b' : '#d63384'
                                                },
                                                '& h1, & h2, & h3': {
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    margin: '0.2rem 0',
                                                    color: darkMode ? '#fff' : '#1a1a1a'
                                                },
                                                fontSize: '0.75rem',
                                                lineHeight: 1.4,
                                                maxHeight: '60px',
                                                overflow: 'hidden',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical'
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: (task.description || task.desc)
                                                    // Упрощенная обработка для таблицы
                                                    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
                                                    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
                                                    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
                                                    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                    .replace(/`([^`]+)`/g, '<code>$1</code>')
                                                    .replace(/^- (.+)$/gm, '<li>$1</li>')
                                                    .replace(/(<li>.*<\/li>\s*)+/g, (match) => `<ul>${match}</ul>`)
                                                    .replace(/\n/g, '<br>')
                                            }}
                                        />
                                    ) : '-'}
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    minWidth: { xs: '80px', md: '120px' }
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: { xs: 0.5, md: 1 },
                                        alignItems: 'center',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        justifyContent: 'center'
                                    }}>
                                        {canDragTask(task) ? (
                                            <StatusChipWithMenu
                                                task={task}
                                                darkMode={darkMode}
                                                onStatusChange={(newStatus) => handleStatusChange(task, newStatus)}
                                            />
                                        ) : (
                                            <Chip
                                                label={task.statusTitle}
                                                sx={{
                                                    backgroundColor: task.statusColor,
                                                    color: 'white',
                                                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                                                    fontWeight: 600,
                                                    height: { xs: 20, md: 24 }
                                                }}
                                            />
                                        )}
                                        {/* Индикация просроченных задач */}
                                        {task.deadline &&
                                            new Date(task.deadline) < new Date() &&
                                            task.status !== 'done' &&
                                            task.status !== 'cancelled' && (
                                                <Chip
                                                    label={window.innerWidth < 600 ? "ПРОС." : "ПРОСРОЧЕНО"}
                                                    size="small"
                                                    sx={{
                                                        height: { xs: 18, md: 20 },
                                                        fontSize: { xs: '0.6rem', md: '0.65rem' },
                                                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                                                        color: '#f44336',
                                                        border: '1px solid rgba(244, 67, 54, 0.5)',
                                                        fontWeight: 700
                                                    }}
                                                />
                                            )}
                                    </Box>
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    color: darkMode ? '#bbb' : '#555',
                                    minWidth: { xs: '80px', md: '100px' },
                                    display: { xs: 'none', sm: 'table-cell' }
                                }}>
                                    {task.creator || '-'}
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    color: darkMode ? '#bbb' : '#555',
                                    minWidth: { xs: '80px', md: '100px' },
                                    display: { xs: 'none', sm: 'table-cell' }
                                }}>
                                    {task.created_at ? formatDate(new Date(task.created_at)) : task.created}
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    color: darkMode ? '#bbb' : '#555',
                                    minWidth: { xs: '80px', md: '100px' },
                                    maxWidth: { xs: '100px', md: '120px' },
                                    wordBreak: 'break-word'
                                }}>
                                    {task.responsible || '-'}
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    color: darkMode ? '#bbb' : '#555',
                                    minWidth: { xs: '80px', md: '100px' }
                                }}>
                                    {task.deadline ? formatDate(new Date(task.deadline)) : '-'}
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    color: darkMode ? '#bbb' : '#555',
                                    minWidth: { xs: '80px', md: '100px' },
                                    display: { xs: 'none', lg: 'table-cell' }
                                }}>
                                    {task.editor || '-'}
                                </TableCell>
                                <TableCell sx={{
                                    ...dataCellStyle,
                                    color: darkMode ? '#bbb' : '#555',
                                    minWidth: { xs: '80px', md: '100px' },
                                    display: { xs: 'none', lg: 'table-cell' }
                                }}>
                                    {task.edited_at ? formatDate(new Date(task.edited_at)) : '-'}
                                </TableCell>

                                <TableCell sx={{
                                    ...dataCellStyle,
                                    minWidth: { xs: '80px', md: '100px' }
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: { xs: 0.25, md: 0.5 },
                                        flexWrap: 'wrap'
                                    }}>
                                        {/* Кнопка показать на карте */}
                                        {task.type === 'contour' && task.data?.objects?.pillars && task.data?.objects?.pillars.length > 0 && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleFocusOnTaskArea(task)}
                                                sx={{
                                                    color: darkMode ? '#4fc3f7' : '#0277bd',
                                                    '&:hover': {
                                                        bgcolor: darkMode
                                                            ? 'rgba(79, 195, 247, 0.08)'
                                                            : 'rgba(2, 119, 189, 0.04)',
                                                        color: darkMode ? '#29b6f6' : '#0277bd'
                                                    },
                                                    p: { xs: 0.25, md: 0.5 }
                                                }}
                                                title="Показать на карте"
                                            >
                                                <LocationIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                                            </IconButton>
                                        )}
                                        {(task.type === 'contour' || task.type === 'points') && Object.keys(task.data.objects).some(type => task.data.objects[type]?.length > 0) && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewSpecs(task)}
                                                sx={{
                                                    color: darkMode ? '#ce93d8' : '#9c27b0',
                                                    '&:hover': {
                                                        bgcolor: darkMode
                                                            ? 'rgba(156, 39, 176, 0.08)'
                                                            : 'rgba(156, 39, 176, 0.04)',
                                                        color: darkMode ? '#ba68c8' : '#8e24aa'
                                                    },
                                                    p: { xs: 0.25, md: 0.5 }
                                                }}
                                            >
                                                <AccountTreeIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                                            </IconButton>
                                        )}
                                        {canEditTask(task) ? (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditTask(task)}
                                                sx={{
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    '&:hover': {
                                                        bgcolor: darkMode
                                                            ? 'rgba(25, 118, 210, 0.08)'
                                                            : 'rgba(25, 118, 210, 0.04)',
                                                        color: '#1976d2'
                                                    },
                                                    p: { xs: 0.25, md: 0.5 }
                                                }}
                                            >
                                                <EditIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                                            </IconButton>
                                        ) : (
                                            <Typography variant="caption" sx={{
                                                color: darkMode ? '#666' : '#999',
                                                fontStyle: 'italic',
                                                fontSize: { xs: '0.65rem', md: '0.75rem' },
                                                display: { xs: 'none', sm: 'inline' }
                                            }}>
                                                Нет прав
                                            </Typography>
                                        )}
                                        {canDeleteTask(task) && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteTask(task.id)}
                                                sx={{
                                                    color: '#f44336',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(244, 67, 54, 0.08)'
                                                    },
                                                    p: { xs: 0.25, md: 0.5 }
                                                }}
                                            >
                                                <DeleteIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TableView;
